import { DateFormats, formatDate } from '@strata/helper-functions';
import { GridWithSearch, SecondaryData } from '@strata/react-grid';
import { StatusIndicator } from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo, useState } from 'react';

import { OrtfOutboundFileDetailsTab } from './OrtfOutboundFileDetailsTab';

import { handleError } from 'services/Error';
import { useGetCompassUserNameMapQuery } from 'store/api/authApi';
import { useGetProcessingFilesQuery, useGetSentFilesQuery } from 'store/api/ndpApi';

const sentFileStatus = {
    indicatorStatus: StatusIndicator.Status.DEFAULT,
    message: 'Sent',
};

const processingFileStatus = {
    indicatorStatus: StatusIndicator.Status.IN_PROGRESS,
    message: 'Processing',
};

const sentColumns = [
    {
        accessorKey: 'receivingPharmacy',
        header: 'Receiving Pharmacy',
        size: 200,
    },
    {
        accessorKey: 'payer',
        header: 'Payer',
        size: 160,
    },
    {
        accessorKey: 'prescriptions',
        header: 'Prescriptions',
        size: 120,
    },
    {
        accessorKey: 'sentBy',
        header: 'Sent By',
        size: 180,
    },
    {
        accessorKey: 'fileName',
        header: 'File Name',
        size: 200,
    },
    {
        accessorKey: 'sentDate',
        header: 'Sent Date',
        size: 140,
        cell: ({ getValue }) => {
            const sentDate = getValue();
            return sentDate ? (
                <SecondaryData data={formatDate(sentDate, DateFormats.DATE)} />
            ) : undefined;
        },
    },
    {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        cell: ({ getValue }) => {
            const status = getValue();
            if (!status) {
                return undefined;
            }

            return (
                <StatusIndicator
                    message={status.message}
                    status={status.indicatorStatus ?? StatusIndicator.Status.DEFAULT}
                />
            );
        },
    },
];

const sentSortDefaults = [{ id: 'sentDate', desc: true }];

const mapSentFileRow = (row, userNameMap) => ({
    ...row,
    receivingPharmacy: row.receivingPharmacy,
    sentBy: userNameMap?.[row.sentByUpn] ?? row.sentByUpn,
    isProcessing: false,
    status: sentFileStatus,
});

const mapProcessingFileRow = (row) => ({
    ...row,
    receivingPharmacy: row.pharmacy,
    isProcessing: true,
    status: processingFileStatus,
});

export const OrtfSentFilesTab = () => {
    const { showOrtfToolV2 = false } = useFlags();
    const [selectedSentFile, setSelectedSentFile] = useState(null);
    const { data: sentFiles = [], isFetching: isFetchingSentFiles } = useGetSentFilesQuery();
    const { data: processingFiles = [], isFetching: isFetchingProcessingFiles } =
        useGetProcessingFilesQuery(undefined, {
            skip: !showOrtfToolV2,
        });
    const { data: userNameMap } = useGetCompassUserNameMapQuery();

    const displayFiles = useMemo(() => {
        const processingRows = showOrtfToolV2 ? processingFiles.map(mapProcessingFileRow) : [];
        const sentRows = sentFiles.map((row) => mapSentFileRow(row, userNameMap));

        return [...processingRows, ...sentRows];
    }, [processingFiles, sentFiles, showOrtfToolV2, userNameMap]);

    const isFetching = isFetchingSentFiles || (showOrtfToolV2 && isFetchingProcessingFiles);

    const handleRowClick = (row) => {
        if (row.isProcessing) {
            return;
        }

        if (!row?.id) {
            handleError('Unable to open scripts for this file (missing outbound file id).');
            return;
        }

        setSelectedSentFile(row);
    };

    if (selectedSentFile) {
        return (
            <OrtfOutboundFileDetailsTab
                readOnly
                onBack={() => setSelectedSentFile(null)}
                outboundFile={{
                    ...selectedSentFile,
                    pharmacy: selectedSentFile.receivingPharmacy,
                }}
                backLabel="SENT FILES"
                title="SENT FILE DETAILS"
                outboundFileStatus={sentFileStatus}
                createdDate={formatDate(selectedSentFile.sentDate, DateFormats.DATE_TIME)}
                fileDate={formatDate(selectedSentFile.sentDate, DateFormats.DATE)}
            />
        );
    }

    return (
        <div className="padding__around--medium">
            <GridWithSearch
                columns={sentColumns}
                data={displayFiles}
                enableSorting
                isLoading={isFetching}
                onRowClick={handleRowClick}
                sortProperties={sentSortDefaults}
            />
        </div>
    );
};
